/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021-2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 *
 */

/**
 * @module bulb-video/bulbvideoediting
 */

import {
  ViewDowncastWriter, Plugin, toWidget, Widget,
} from 'ckeditor5';

import { InsertBulbVideoCommand } from './bulbvideocommand';
import { getBlockVersion, getJWPlayer, setOverlay } from '../utils';

/**
 * The bulb video editing infrastructure, this will create the block using the converters.
 *
 * @extends Plugin
 */
export default class BulbVideoEditing extends Plugin {

  /**
   * @inheritDoc
   *
   */
  public static get requires() {
    return [Widget] as const;
  }

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbVideoEditing' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    this.defineSchema();
    this.defineConverters();

    this.editor.commands.add('insertVideo', new InsertBulbVideoCommand(this.editor));
  }

  /**
   * Method that defines the schema four our video block.
   *
   * @private
   */
  private defineSchema(): void {
    const { schema } = this.editor.model;
    schema.register('video', {
      isObject: true,
      isBlock: true,
      allowWhere: '$block',
      allowAttributes: ['bulb-block-id', 'blockAlignment', 'width', 'bulb-block-version', 'video-type'],
    });
  }

  /**
   * Method that creates and initializes the downcast ( editing and data ) and
   * upcast converters for our video block.
   *
   * @private
   */
  private defineConverters(): void {
    const { conversion } = this.editor;
    const { editor } = this;

    conversion.for('upcast').elementToElement({
      view: {
        name: 'figure',
        classes: 'video',
      },
      model: (viewElement, { writer: modelWriter }) => modelWriter.createElement('video', {
        'bulb-block-id': viewElement.getAttribute('bulb-block-id'),
        'bulb-block-version': getBlockVersion(CKEBulbData, viewElement),
      }),
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'video',
      view: (modelElement, { writer: viewWriter }) => viewWriter.createContainerElement('figure', {
        class: 'video',
        'bulb-block-id': modelElement.getAttribute('bulb-block-id'),
        'bulb-block-version': getBlockVersion(CKEBulbData, modelElement),
      }),
    });

    conversion.for('editingDowncast').elementToElement({
      model: 'video',
      view: (modelElement, { writer: viewWriter }) => {
        const contentBlockId = modelElement.getAttribute('bulb-block-id') as string;
        const contentBlock = CKEBulbData.page.unit.contentBlocks[contentBlockId];
        const { video } = contentBlock;
        const videoType = contentBlock.video.jspType;
        const playerId = contentBlockId;
        let videoWrapper = null;
        let overlay = null;

        if (videoType === 'TRANSCODING') {
          const assetUrls = video.originalAssetUrl;
          const assetHeights = '720';
          const thumbnail = CKEBulbData.service.ckeStateService.capturedSplashUrl.value;
          videoWrapper = BulbVideoEditing.jwPlayer(viewWriter, playerId, assetUrls, assetHeights, thumbnail, videoType);
        } else if (videoType === 'ASSET') {
          const assetUrls = video.transcodedAssets.map((x) => x.url);
          const assetHeights = video.transcodedAssets.map((x) => x.height);
          const thumbnail = video.splashImage.url;
          videoWrapper = BulbVideoEditing.jwPlayer(viewWriter, playerId, assetUrls, assetHeights, thumbnail, videoType);
        } else {
          // Need to handle Vimeo videos:
          const uiClass = (videoType === 'VIMEO') ? 'vimeo_iframe_wrapper' : 'jwplayer_wrapper';
          videoWrapper = viewWriter.createUIElement('div', {
            class: `${uiClass} no-editor-placeholder`,
          }, function (domDocument) {
            const domElement = this.toDomElement(domDocument);

            if (videoType === 'VIMEO') {
              const vimeoId = contentBlock.video.videoId;
              domElement.innerHTML = ` <iframe src="https://player.vimeo.com/video/${vimeoId}"
                            frameborder="0"
                            allowfullscreen>
                           </iframe>`;
            }
            return domElement;
          });
        }
        if (CKEBulbData.page.editing) {
          overlay = viewWriter.createUIElement('div', {
            class: 'overlay',
            id: `overlay-${contentBlockId}`,
          }, function (domDocument) {
            const domElement = this.toDomElement(domDocument);
            (document as any).editor = editor;
            document.addEventListener('click', setOverlay);
            return domElement;
          });
        }

        const disableDraggingClass = !CKEBulbData.page.editing ? 'bulb-disable-dragging' : '';

        const figure = viewWriter.createContainerElement('figure', {
          class: `video ${disableDraggingClass}`,
          'bulb-block-id': contentBlockId,
          'bulb-block-version': modelElement.getAttribute('bulb-block-version') || 0,
        });

        const bulbBlockResizeContainer = viewWriter.createContainerElement('div', {
          class: 'resize-overlay',
          id: `resize-${contentBlockId}`,
        });

        const uiClass = (videoType === 'VIMEO') ? 'bulb_vimeo_wrapper' : 'bulb_jwplayer_wrapper';
        const container = viewWriter.createContainerElement('div', {
          class: uiClass,
        });
        if (CKEBulbData.page.editing) {
          viewWriter.insert(viewWriter.createPositionAt(container, 'end'), overlay);
        }
        viewWriter.insert(viewWriter.createPositionAt(container, 'end'), videoWrapper);

        viewWriter.insert(viewWriter.createPositionAt(figure, 'end'), bulbBlockResizeContainer);
        viewWriter.insert(viewWriter.createPositionAt(figure, 'end'), container);
        viewWriter.setCustomProperty('video', true, figure);
        const data = {
          viewWriter, modelElement, figure,
        };
        if (videoType === 'TRANSCODING') {
          CKEBulbData.service.ckeStateService.startTranscoding.next(data);
        }
        return toWidget(figure, viewWriter, { label: 'Video Block', hasSelectionHandle: true });
      },
    });
  }

  /**
   * Method that creates and initializes JWPlayer.
   * @param viewWriter
   * @param playerId
   * @param assetUrls
   * @param assetHeights
   * @param thumbnail
   * @param videoType
   *
   */
  private static jwPlayer(viewWriter: ViewDowncastWriter, playerId, assetUrls, assetHeights, thumbnail, videoType: string) {
    return viewWriter.createUIElement('div', {
      class: 'no-editor-placeholder',
    }, function (domDocument) {
      const domElement = this.toDomElement(domDocument);
      domElement.innerHTML = `<div id="${playerId}"></div>`;
      const videoArray = BulbVideoEditing.createJWPlayerVideoArray(assetUrls, assetHeights, videoType);

      // Do a timeout here without a second parameter so that this function runs
      // after the queue. This will ensure that the block is on the page before
      // running this block of code.
      setTimeout(() => {
        const jwplayer = getJWPlayer();
        const jwPlayerInstance = jwplayer(playerId);
        jwPlayerInstance.setup({
          image: thumbnail,
          sources: videoArray,
        });
      });

      return domElement;
    });
  }

  /**
   * Method that creates and populates the audio array of transcoded video assets to initialize JWPlayer with.
   *
   * @param urlArray Array of string urls to the transcoded video assets that are hosted on s3.
   * @param heightArray Array of transcoded video heights.
   * @param videoType video Type
   * @returns {[]} videoArray with all the necessary information to initiailze JWPlayer with.
   *
   * @private
   */
  private static createJWPlayerVideoArray(urlArray, heightArray, videoType: string): any[] {
    // JWPlayer accepts an array of data featuring a url and a height to display in the visual options.
    // We have these, but need to create them.

    if (typeof urlArray === 'string') {
      urlArray = urlArray.split(',');
      heightArray = heightArray.split(',');
    }

    let videoArray = [];

    for (let i = 0; i < urlArray.length; i++) {
      const url = urlArray[i];
      const height = heightArray[i];

      const videoObject = { file: url, label: height };
      videoArray.push(videoObject);
    }
    if (videoType === 'ASSET') {
      videoArray = videoArray.sort((a, b) => a.label - b.label).reverse();
      videoArray[0].default = true;
    }

    return videoArray;
  }

  /**
   * @inheritDoc
   */
  public destroy(): void {
    document.removeEventListener('click', setOverlay);
  }
}
